import { useEffect, useRef, useState } from "react";
import { ImagePlus, Upload, X } from "lucide-react";
import { api, errMsg, uploadTeamLogo } from "../lib/api";
import { Crest } from "./kit";
import { Button } from "./ui/button";
import { toast } from "sonner";

export const TeamLogoCard = ({ team, onChange }) => {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const id = team.id;
  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const choose = (e) => {
    const next = e.target.files?.[0];
    e.target.value = "";
    setError(""); setConfirmRemove(false);
    if (!next) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(next.type)) return setError("Choose a PNG, JPG or WebP image.");
    if (next.size > 5 * 1024 * 1024 || !next.size) return setError("Choose an image up to 5 MB (not an empty file).");
    setFile(next);
  };
  const save = async () => {
    setError("");
    try {
      const result = await uploadTeamLogo(id, file, setProgress);
      onChange({ ...team, logo: result.logo });
      setFile(null); toast.success("Team logo updated across TRO");
    } catch (e) { setError(errMsg(e)); }
    finally { setProgress(null); }
  };
  const remove = async () => {
    setError(""); setProgress({ percent: 0, label: "Removing logo…" });
    try {
      await api.delete(`/teams/${id}/logo`);
      onChange({ ...team, logo: null }); setConfirmRemove(false); toast.success("Team initials restored");
    } catch (e) { setError(errMsg(e)); }
    finally { setProgress(null); }
  };
  return (
    <section className="tro-card min-w-0 space-y-4 p-4" data-testid={`team-logo-card-${id}`} aria-busy={!!progress}>
      <div className="flex items-center gap-3">
        <Crest club={{ ...team, logo: preview || team.logo }} size={56} testid={`team-logo-preview-${id}`} />
        <div className="min-w-0">
          <div className="tro-label" data-testid={`team-category-${id}`}>{team.category}</div>
          <h2 className="break-words text-base font-bold" data-testid={`team-name-${id}`}>{team.name}</h2>
          <p className="text-xs text-[#6C757D]" data-testid={`team-details-${id}`}>{team.club_name} · {team.player_count ?? 0} players</p>
        </div>
      </div>
      {team.can_manage_logo && <>
        <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={choose} disabled={!!progress} aria-label={`Choose logo for ${team.name}`} data-testid={`team-logo-file-${id}`} />
        <p className="text-xs text-[#6C757D]" data-testid={`team-logo-help-${id}`}>PNG, JPG or WebP · up to 5 MB. Shown across matches and standings.</p>
        {file && <p className="truncate text-xs font-semibold" data-testid={`team-logo-filename-${id}`}>{file.name} · ready to save</p>}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => input.current.click()} disabled={!!progress} data-testid={`team-logo-choose-${id}`}><ImagePlus className="mr-1.5 h-4 w-4" />{team.logo || file ? "Replace logo" : "Upload logo"}</Button>
          {file && <><Button size="sm" onClick={save} disabled={!!progress} data-testid={`team-logo-save-${id}`}><Upload className="mr-1.5 h-4 w-4" />Save logo</Button><Button size="sm" variant="ghost" onClick={() => { setFile(null); setError(""); }} disabled={!!progress} data-testid={`team-logo-cancel-${id}`} aria-label="Cancel selected logo"><X className="h-4 w-4" /></Button></>}
          {team.logo && !file && !confirmRemove && <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(true)} disabled={!!progress} data-testid={`team-logo-remove-${id}`}>Remove</Button>}
        </div>
        {confirmRemove && <div className="rounded-lg border border-[#E6E9ED] p-3" data-testid={`team-logo-remove-confirmation-${id}`}>
          <p className="mb-2 text-sm">Remove this logo and use the team initials?</p>
          <div className="flex gap-2"><Button size="sm" variant="destructive" disabled={!!progress} onClick={remove} data-testid={`team-logo-confirm-remove-${id}`}>Remove logo</Button><Button size="sm" variant="outline" disabled={!!progress} onClick={() => setConfirmRemove(false)} data-testid={`team-logo-keep-${id}`}>Keep logo</Button></div>
        </div>}
        {progress && <div aria-live="polite" data-testid={`team-logo-upload-status-${id}`}>
          <div className="mb-1 flex justify-between gap-2 text-xs"><span>{progress.label}</span><b>{progress.percent}%</b></div>
          <progress value={progress.percent} max="100" className="h-2 w-full accent-[#17191C]" aria-label="Logo upload progress" data-testid={`team-logo-progress-${id}`} />
        </div>}
        {error && <p role="alert" className="text-sm font-semibold text-[#C81E2B]" data-testid={`team-logo-error-${id}`}>{error}</p>}
      </>}
    </section>
  );
};
